import React, { useState } from "react";
import { format } from "date-fns";
import toast, { Toaster } from "react-hot-toast";
import Seo from "components/Seo";
import CustomerLayout from "./customerLayout";
import Button from "../../-components/common/Button";
import Spinner from "components/Spinner";
import DayPickerInput from "../../-components/common/DayPickerInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import Pagination from "components/Pagination";
import { toastErrorSyle } from "hooks/utils/util";
import { useGetDriverInfoWarehouses, useGetDriverInfoSearch, DriverInfoSearchParams } from "hooks/morning/useDriverInfo";

const LIMIT = 10;

const MorningDriverInfo = () => {
    const [portName, setPortName] = useState("");
    const [warehouseNo, setWarehouseNo] = useState("");
    const [workDate, setWorkDate] = useState<Date | undefined>(undefined);
    // 검색 버튼으로 확정된 조건(라이브 입력과 분리 → 페이지 이동 시 조건 고정)
    const [searchedParams, setSearchedParams] = useState<Omit<DriverInfoSearchParams, "page" | "limit"> | null>(null);
    const [page, setPage] = useState(1);

    const { data: warehouses } = useGetDriverInfoWarehouses();
    const { data, isFetching, isError } = useGetDriverInfoSearch(
        searchedParams ? { ...searchedParams, page, limit: LIMIT } : null,
    );
    const results = searchedParams ? data?.data_list ?? [] : null;

    // 항구명 옵션(중복 제거) / 선택한 항구의 입고지 목록 / 선택한 입고지(주소 표시용)
    // 정렬을 관리자 화면과 동일하게 통일 (한글 가나다순)
    const portOptions = Array.from(new Set((warehouses ?? []).map(w => w.port_name).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, "ko"),
    );
    const filteredWarehouses = (warehouses ?? [])
        .filter(w => w.port_name === portName)
        .sort((a, b) => a.name.localeCompare(b.name, "ko"));
    const selectedWarehouse = (warehouses ?? []).find(w => String(w.warehouse_no) === warehouseNo);
    const fullAddress = selectedWarehouse
        ? [selectedWarehouse.post && `(${selectedWarehouse.post})`, selectedWarehouse.address1, selectedWarehouse.address2]
              .filter(Boolean)
              .join(" ")
        : "";

    const handleSearch = () => {
        if (!warehouseNo) {
            toast.error("입고지명을 선택해주세요.", toastErrorSyle);
            return;
        }
        if (!workDate) {
            toast.error("입고일(배송일)을 선택해주세요.", toastErrorSyle);
            return;
        }
        setPage(1);
        setSearchedParams({
            warehouse_no: warehouseNo,
            work_date: format(workDate, "yyyy-MM-dd"),
        });
    };

    const selectTrigger =
        "relative shadow-none text-[#1B1C1E] text-[18px] flex gap-[10px] rounded-lg focus:ring-2 w-full border border-[#E1E2E4] bg-white h-[52px] px-4";

    return (
        <React.Fragment>
            <Seo type="morning" pageTitle="입고지 배송기사 정보 조회" />
            {isFetching && <Spinner />}
            <CustomerLayout>
                <div className="flex-1 bg-[#f8f8f8] flex flex-col items-center pt-12 responsive1200:pt-[30px] responsive600:px-[4%] responsive600:bg-white">
                    <p className="text-[#1b1c1e] text-[36px] font-bold responsive1200:text-[24px] responsive1200:font-[500]">
                        입고지 배송기사 정보 조회
                    </p>

                    <div className="w-full max-w-[760px] mt-12 responsive1200:mt-[30px]">
                        <div className="bg-white border border-[#E1E2E4] rounded-2xl p-8 responsive600:p-6">
                            <p className="text-[20px] font-bold text-new mb-5 responsive600:text-[18px]">체크사항</p>
                            <ul className="flex flex-col gap-4">
                                <li className="text-[16px] text-[#1B1C1E] responsive600:text-[15px]">
                                    <span className="font-[500]">공통 1.</span> 물품에 쉬핑마크 부착여부
                                </li>
                                <li className="text-[16px] text-[#1B1C1E] responsive600:text-[15px]">
                                    <span className="font-[500]">공통 2.</span> 입고건 특성상 접수자 핸드폰 번호 필수 기재요망
                                </li>
                                <li className="text-[16px] text-[#1B1C1E] responsive600:text-[15px]">
                                    <span className="font-[500]">공통 3.</span> 입고증 동봉여부 (서류봉투에 넣어서 물품에 부착바랍니다.)
                                    <span className="block mt-1 text-[14px] text-[#888] responsive600:text-[13px]">
                                        ※ "입고증"자체를 물품에 부착할 경우 제출을 위한 입고증 분리 과정에서 물품손상우려
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="w-full max-w-[760px] pb-12">
                        <div className="bg-white items-center mt-12 p-10 rounded-2xl flex flex-col mb-6 gap-5 responsive1200:mt-[30px] responsive600:px-0 responsive600:py-0 responsive600:mt-[54px]">
                            {/* 입고지 */}
                            <div className="flex items-center w-full responsive600:flex-col responsive600:gap-[8px]">
                                <div className="text-[18px] font-[500] min-w-[160px] responsive600:self-start responsive600:text-[16px]">
                                    입고지 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <Select
                                    value={portName}
                                    onValueChange={value => {
                                        setPortName(value);
                                        setWarehouseNo(""); // 항구 변경 시 입고지 선택 초기화
                                        setSearchedParams(null); // 조건 변경 시 이전 검색 결과 초기화
                                    }}
                                >
                                    <SelectTrigger className={selectTrigger}>
                                        <SelectValue placeholder="입고지를 선택해주세요." />
                                        <ArrowDown />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        {portOptions.map(port => (
                                            <SelectItem key={port} value={port}>
                                                {port}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* 입고지명 */}
                            <div className="flex items-center w-full responsive600:flex-col responsive600:gap-[8px]">
                                <div className="text-[18px] font-[500] min-w-[160px] responsive600:self-start responsive600:text-[16px]">
                                    입고지명 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <Select
                                    value={warehouseNo}
                                    onValueChange={value => {
                                        setWarehouseNo(value);
                                        setSearchedParams(null); // 조건 변경 시 이전 검색 결과 초기화
                                    }}
                                    disabled={!portName}
                                >
                                    <SelectTrigger className={`${selectTrigger} disabled:bg-[#f4f4f4] disabled:cursor-not-allowed`}>
                                        <SelectValue placeholder={portName ? "입고지명을 선택해주세요." : "입고지를 먼저 선택해주세요."} />
                                        <ArrowDown />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        {filteredWarehouses.map(item => (
                                            <SelectItem key={item.warehouse_no} value={String(item.warehouse_no)}>
                                                {item.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* 주소 (입고지 선택 시 view 모드로 표시) */}
                            {selectedWarehouse && (
                                <div className="flex items-center w-full responsive600:flex-col responsive600:gap-[8px]">
                                    <div className="text-[18px] font-[500] min-w-[160px] responsive600:self-start responsive600:text-[16px]">
                                        주소
                                    </div>
                                    <div className="w-full min-h-[52px] flex items-center px-4 py-2 bg-[#f8f8f8] border border-[#E1E2E4] rounded-lg text-[16px] text-[#555] break-all">
                                        {fullAddress || "등록된 주소가 없습니다."}
                                    </div>
                                </div>
                            )}

                            {/* 입고일(배송일) */}
                            <div className="flex items-center w-full responsive600:flex-col responsive600:gap-[8px]">
                                <div className="text-[18px] font-[500] min-w-[160px] responsive600:self-start responsive600:text-[16px]">
                                    입고일(배송일) <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <div className="flex items-center bg-white h-[52px] px-4 border border-[#E1E2E4] rounded-lg w-full">
                                    <DayPickerInput
                                        initialDate={workDate}
                                        placeholder="입고일(배송일)을 선택해주세요."
                                        twoCalendar
                                        onDateChange={date => {
                                            setWorkDate(date);
                                            setSearchedParams(null); // 조건 변경 시 이전 검색 결과 초기화
                                        }}
                                    />
                                </div>
                            </div>

                            {/* 입고지명별 안내 문구 */}
                            {warehouseNo === "7" && (
                                <div className="w-full text-[15px] text-[#1B1C1E] responsive600:text-[14px] break-keep mt-5">
                                    <p className="font-[500] text-new mb-1">키오스크 등록 필수</p>
                                    <p>일신모닝택배 운송장 비고(메모)에 키오스크 등록완료 및 입고 실화주 기재 요망</p>
                                </div>
                            )}
                            {warehouseNo === "5" && (
                                <div className="w-full text-[15px] text-[#1B1C1E] responsive600:text-[14px] break-keep mt-5">
                                    <p className="font-[500] text-new mb-1">키오스크 등록 필수</p>
                                    <p>
                                        한진 CFS 사이트 차량 입출고접수 입력란 중 &quot;운송사&quot; 란에 일신모닝택배 송장 보내는고객
                                        상호 기재 요망
                                    </p>
                                </div>
                            )}
                            {warehouseNo === "6" && (
                                <div className="w-full text-[15px] text-[#1B1C1E] responsive600:text-[14px] break-keep mt-5">
                                    <p className="font-[500] text-new mb-1">물품에 입고증 동봉 or 차량등록</p>
                                    <p>- 입고증은 서류봉투에 넣어서 물품에 부착요망</p>
                                    <p className="mt-2">- 차량등록시에는 일신모닝택배 운송장 비고(메모)에 실화주 기재 요망</p>
                                </div>
                            )}
                        </div>

                        <Button size="L" type="button" onClick={handleSearch} className="w-full border-none bg-new text-white">
                            검색
                        </Button>
                    </div>

                    {/* 검색 결과 */}
                    {results !== null && (
                        <div className="w-full max-w-[1000px] my-12 responsive1200:px-[4%] responsive600:px-0">
                            <p className="text-[20px] font-bold mb-4">담당기사 목록</p>
                            {isError ? (
                                <div className="bg-white rounded-2xl py-[80px] text-center text-[18px] text-[#666]">
                                    조회 중 오류가 발생했습니다.
                                </div>
                            ) : results.length > 0 ? (
                                <>
                                    <div className="overflow-x-auto bg-white rounded-2xl">
                                        <table className="min-w-[800px] w-full border-t border-t-[3px] border-t-[#999999] table-fixed">
                                            <colgroup>
                                                <col width="16%" />
                                                <col width="20%" />
                                                <col width="15%" />
                                                <col width="15%" />
                                                <col width="18%" />
                                                <col width="16%" />
                                            </colgroup>
                                            <thead>
                                                <tr className="border-b border-[#dddddd] bg-[#f8f8f8]">
                                                    <th className="font-[500] py-[15px] text-[#666666]">입고지</th>
                                                    <th className="font-[500] py-[15px] text-[#666666]">입고지명</th>
                                                    <th className="font-[500] py-[15px] text-[#666666]">입고일(배송일)</th>
                                                    <th className="font-[500] py-[15px] text-[#666666]">담당기사</th>
                                                    <th className="font-[500] py-[15px] text-[#666666]">전화번호</th>
                                                    <th className="font-[500] py-[15px] text-[#666666]">차량번호</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {results.map((row, index) => (
                                                    <tr key={index} className="border-b border-[#dddddd] text-center">
                                                        <td className="py-[16px]">{row.port_name}</td>
                                                        <td className="py-[16px]">{row.warehouse_name}</td>
                                                        <td className="py-[16px]">{row.work_date}</td>
                                                        <td className="py-[16px]">{row.driver_name}</td>
                                                        <td className="py-[16px]">{row.phone}</td>
                                                        <td className="py-[16px]">{row.car_no}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-8">
                                        <Pagination
                                            current={page}
                                            total={data?.total_count || 1}
                                            pageSize={LIMIT}
                                            onChange={newPage => setPage(newPage)}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="bg-white rounded-2xl py-[80px] text-center text-[18px] text-[#666]">
                                    조회된 담당기사가 없습니다.
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <Toaster position="bottom-center" reverseOrder={false} />
            </CustomerLayout>
        </React.Fragment>
    );
};

export default MorningDriverInfo;
